import { useMemo, type ComponentType, type SVGProps } from 'react';
import { Link } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatQty, variantLabel } from '@pos/core';
import { RUBROS } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { BarChart } from '../../ui/BarChart.tsx';
import { Card, PageHeader, SectionTitle, Spinner, Stat, cx } from '../../ui/index.tsx';
import { AlertIcon, CashIcon, ChartIcon, CloudIcon, ReceiptIcon, TrendIcon, UsersIcon } from '../../ui/icons.tsx';
import { useCatalog, useMoney } from '../products/data.ts';
import { isAlert, useStockMap, useStockRows } from '../stock/data.ts';
import { useBalances } from '../customers/data.ts';
import { fetchSummary, fetchTopProducts, fillDays, useReport, type ReportSummary, type TopProduct } from '../reports/data.ts';
import { addDays, change, localDate, makePeriod } from '../reports/period.ts';
import { fetchConflicts, usePendingRejections } from '../sync/data.ts';

const weekday = new Intl.DateTimeFormat('es-AR', { weekday: 'short', day: 'numeric', timeZone: 'UTC' });

/** Tablero del dueño: cómo va el día y el mes, y lo que necesita atención. */
export function HomePage() {
  const { business, membership } = useSession();
  const money = useMoney();
  const tz = business.timezone || 'America/Argentina/Buenos_Aires';
  const today = localDate(new Date(), tz);

  const periods = useMemo(() => {
    const monthStart = `${today.slice(0, 8)}01`;
    const prevMonthStart = `${addDays(monthStart, -1).slice(0, 8)}01`;
    // Mes anterior hasta el mismo día (para comparar parejo).
    const prevSameDay = addDays(prevMonthStart, Math.min(Number(today.slice(8)) - 1, Number(addDays(monthStart, -1).slice(8)) - 1));
    return {
      last14: makePeriod(addDays(today, -13), today, tz),
      month: makePeriod(monthStart, today, tz),
      prevMonth: makePeriod(prevMonthStart, prevSameDay, tz),
      last7: makePeriod(addDays(today, -6), today, tz),
    };
  }, [today, tz]);

  const report = useReport(
    async (id) => {
      const [recent, month, prevMonth, top] = await Promise.all([
        fetchSummary(id, periods.last14),
        fetchSummary(id, periods.month),
        fetchSummary(id, periods.prevMonth),
        fetchTopProducts(id, periods.last7, 'total', 5),
      ]);
      return { recent, month, prevMonth, top };
    },
    [periods],
  );

  const catalog = useCatalog();
  const stock = useStockMap();
  const stockRows = useStockRows(catalog, stock);
  const alerts = stockRows.filter((r) => r.item.product.active && r.variant.active && isAlert(r.status)).length;
  const balances = useBalances();
  const owed = [...(balances?.values() ?? [])].reduce((a, b) => a + Math.max(0, b), 0);
  const local = useLiveQuery(async () => {
    const [openCash, pending, devices, registers] = await Promise.all([
      db.cash_sessions.where('business_id').equals(business.id).filter((s) => s.closed_at === null).toArray(),
      db.outbox.where('status').equals('pending').count(),
      db.devices.where('business_id').equals(business.id).toArray(),
      db.registers.where('business_id').equals(business.id).toArray(),
    ]);
    // Nombre de cada caja abierta (y del dispositivo, si es una caja de un solo dispositivo).
    const registerName = (id: string | null | undefined) => registers.find((r) => r.id === id)?.name ?? registers.find((r) => r.is_default)?.name ?? 'Caja';
    const openNames = openCash.map((s) =>
      s.device_only ? `${registerName(s.register_id)} (${devices.find((d) => d.id === s.device_id)?.name ?? 'un dispositivo'})` : registerName(s.register_id),
    );
    return { openNames, pending };
  }, [business.id]);

  // Sincronización: lo que falta enviar de acá y lo que hay para revisar de todo el negocio.
  const quarantine = usePendingRejections(business.id);
  const conflicts = useReport((id) => fetchConflicts(id), []);
  const toReview = (quarantine?.length ?? 0) + (conflicts.state === 'ready' ? conflicts.data.length : 0);
  const syncValue = !local
    ? '–'
    : [local.pending ? `${local.pending} sin enviar` : 'Todo enviado', toReview ? `${toReview} para revisar` : ''].filter(Boolean).join(' · ');

  const firstName = membership.display_name.split(' ')[0];
  const date = new Intl.DateTimeFormat('es-AR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: tz }).format(new Date());

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <PageHeader
        title={`Hola, ${firstName}`}
        description={`${date.charAt(0).toUpperCase()}${date.slice(1)} · ${business.name} · ${RUBROS[business.rubro]}`}
      />

      {report.state === 'loading' && (
        <div className="flex justify-center py-8 text-slate-500">
          <Spinner />
        </div>
      )}
      {(report.state === 'offline' || report.state === 'error') && (
        <Card className="text-sm text-slate-600">
          {report.state === 'offline'
            ? 'Sin conexión: los números del tablero suman todos los dispositivos y necesitan internet. Lo de abajo es de este dispositivo.'
            : `No se pudieron cargar los números: ${report.message}`}
        </Card>
      )}
      {report.state === 'ready' && (
        <Dashboard data={report.data} today={today} yesterday={addDays(today, -1)} days={fillDays(periods.last14, report.data.recent.by_day)} money={money} />
      )}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Tile
          to="/stock?estado=alerts"
          icon={AlertIcon}
          label="Stock con alertas"
          value={catalog && stock ? (alerts === 0 ? 'Todo en orden' : `${alerts} ${alerts === 1 ? 'producto' : 'productos'}`) : '–'}
          warn={alerts > 0}
        />
        <Tile
          to="/caja/historial"
          icon={CashIcon}
          label="Cajas abiertas"
          value={local ? (local.openNames.length === 0 ? 'Ninguna' : local.openNames.join(', ')) : '–'}
        />
        <Tile to="/clientes" icon={UsersIcon} label="Te deben (cuenta corriente)" value={money(owed)} />
        <Tile to="/sincronizacion" icon={CloudIcon} label="Sincronización" value={syncValue} warn={!!local?.pending || toReview > 0} />
      </div>
    </div>
  );
}

function Tile({ to, icon: Icon, label, value, warn }: { to: string; icon: ComponentType<SVGProps<SVGSVGElement>>; label: string; value: string; warn?: boolean }) {
  return (
    <Link
      to={to}
      title={value}
      className={cx('group flex items-center gap-3 rounded-xl bg-white p-4 shadow-card ring-1 transition hover:ring-brand', warn ? 'ring-amber-300' : 'ring-slate-200/80')}
    >
      <span className={cx('flex size-10 shrink-0 items-center justify-center rounded-lg', warn ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' : 'bg-slate-100 text-slate-600')}>
        <Icon className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm text-slate-500">{label}</span>
        <span className={cx('block truncate font-semibold', warn ? 'text-amber-700' : 'text-slate-900')}>{value}</span>
      </span>
      <span aria-hidden="true" className="text-slate-400 transition-transform group-hover:translate-x-0.5">
        →
      </span>
    </Link>
  );
}

function Delta({ value, suffix }: { value: number | null; suffix: string }) {
  if (value === null) return <span className="text-xs text-slate-500">sin datos para comparar</span>;
  const up = value >= 0;
  return (
    <span className={cx('text-xs font-medium', up ? 'text-emerald-700' : 'text-red-700')}>
      {up ? '▲' : '▼'} {Math.abs(Math.round(value))} % {suffix}
    </span>
  );
}

function Dashboard({
  data,
  today,
  yesterday,
  days,
  money,
}: {
  data: { recent: ReportSummary; month: ReportSummary; prevMonth: ReportSummary; top: TopProduct[] };
  today: string;
  yesterday: string;
  days: Array<{ day: string; total: number; count: number; profit: number }>;
  money: (c: number) => string;
}) {
  const t = days.find((d) => d.day === today) ?? { total: 0, count: 0, profit: 0 };
  const y = days.find((d) => d.day === yesterday) ?? { total: 0, count: 0, profit: 0 };
  const m = data.month.totals;
  const pm = data.prevMonth.totals;
  const hasCosts = m.revenue_with_cost > 0;

  const topMax = Math.max(1, ...data.top.map((p) => p.total));

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Vendido hoy" icon={CashIcon} value={money(t.total)} foot={<Delta value={change(t.total, y.total)} suffix="vs. ayer" />} />
        <Stat label="Ventas hoy" icon={ReceiptIcon} value={String(t.count)} foot={`Ticket promedio ${money(t.count ? Math.round(t.total / t.count) : 0)}`} />
        <Stat label="Vendido este mes" icon={ChartIcon} value={money(m.total)} foot={<Delta value={change(m.total, pm.total)} suffix="vs. mismos días del mes pasado" />} />
        <Stat
          label="Ganancia del mes"
          icon={TrendIcon}
          tone="green"
          value={hasCosts ? money(m.profit) : '—'}
          foot={
            <>
              {hasCosts ? `Margen ${Math.round((m.profit / m.revenue_with_cost) * 100)} %` : 'Cargá costos para verla'}
              {m.products_without_cost > 0 && hasCosts && ` · ${m.products_without_cost} productos sin costo`}
            </>
          }
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card>
          <SectionTitle
            action={
              <Link to="/reportes" className="text-sm font-medium text-brand">
                Ver reportes →
              </Link>
            }
          >
            Ventas de los últimos 14 días
          </SectionTitle>
          <BarChart
            ariaLabel="Ventas por día de los últimos 14 días"
            format={money}
            bars={days.map((d) => ({
              key: d.day,
              label: weekday.format(new Date(`${d.day}T12:00:00Z`)),
              value: d.total,
              tooltip: `${weekday.format(new Date(`${d.day}T12:00:00Z`))}: ${money(d.total)} · ${d.count} ventas`,
            }))}
          />
        </Card>
        <Card>
          <SectionTitle>Más vendidos (7 días)</SectionTitle>
          {data.top.length === 0 ? (
            <p className="text-sm text-slate-500">Todavía no hay ventas esta semana.</p>
          ) : (
            <ol className="space-y-3">
              {data.top.map((p, i) => {
                const attrs = variantLabel(p.attributes);
                return (
                  <li key={p.variant_id}>
                    <div className="flex items-baseline justify-between gap-2 text-sm">
                      <span className="flex min-w-0 items-baseline gap-2">
                        <span className="w-4 shrink-0 text-right text-xs font-semibold text-slate-400 tabular-nums">{i + 1}</span>
                        <span className="min-w-0 truncate font-medium text-slate-800">
                          {p.product}
                          {attrs ? ` · ${attrs}` : ''}
                        </span>
                      </span>
                      <span className="shrink-0 font-medium tabular-nums">{money(p.total)}</span>
                    </div>
                    {/* Barra con lo que vendió cada uno respecto del primero. */}
                    <div className="mt-1.5 flex items-center gap-2 pl-6">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                        <div className="chart-fill h-full rounded-full" style={{ width: `${Math.max(3, Math.round((p.total / topMax) * 100))}%` }} />
                      </div>
                      <span className="shrink-0 text-xs text-slate-500 tabular-nums">{formatQty(p.qty_milli, p.unit_type)}</span>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </Card>
      </div>
    </>
  );
}
